import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Sparkles, PackageOpen, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/api";

export default function LandingPage() {
  const { slug } = useParams();
  const [landing, setLanding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/landings/${encodeURIComponent(slug)}`)
      .then(({ data }) => setLanding(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nome e telefono sono obbligatori.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", { ...form, landing_slug: slug });
      setDone(true);
      toast.success("Prenotazione inviata! Ti contatteremo a breve.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante l'invio.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-10 h-10 border-4 border-brand/30 border-t-brand rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !landing) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center text-center px-5 py-24" data-testid="landing-not-found">
          <PackageOpen className="w-12 h-12 text-navy-soft mb-4" />
          <h1 className="font-heading font-bold text-2xl text-navy">Promozione non trovata</h1>
          <p className="mt-2 text-navy-soft text-sm">Il link potrebbe essere scaduto o non valido.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const services = (landing.items || []).map((i) => i.name).filter(Boolean);

  const parsePrice = (s) => {
    if (!s) return null;
    const m = String(s).replace(/\./g, "").replace(",", ".").match(/[\d.]+/);
    return m ? parseFloat(m[0]) : null;
  };
  const discountPct = (full, price) => {
    const f = parsePrice(full);
    const p = parsePrice(price);
    if (f && p && f > p) return Math.round(((f - p) / f) * 100);
    return null;
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="landing-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line bg-navy text-white">
        {landing.hero_image && (
          <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${landing.hero_image})` }} />
        )}
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          {landing.badge && (
            <div className="inline-flex items-center gap-2 bg-brand text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full" data-testid="landing-badge">
              <Sparkles className="w-3.5 h-3.5" /> {landing.badge}
            </div>
          )}
          <h1 className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="landing-title">
            {landing.title}
          </h1>
          {landing.subtitle && <p className="mt-4 text-lg sm:text-xl text-white/80 max-w-2xl">{landing.subtitle}</p>}
          <a href="#prenota" className="mt-8 inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-7 py-3.5 rounded-full transition-colors soft-shadow" data-testid="landing-cta-scroll">
            Prenota la promozione
          </a>
        </div>
      </section>

      <section className="max-w-5xl mx-auto w-full px-5 sm:px-8 py-14 flex-1">
        {landing.description && (
          <p className="text-lg text-navy-soft leading-relaxed max-w-3xl">{landing.description}</p>
        )}

        {/* Benefits */}
        {landing.benefits && landing.benefits.length > 0 && (
          <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="landing-benefits">
            {landing.benefits.map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-surface rounded-xl border border-line px-4 py-3">
                <CheckCircle2 className="w-5 h-5 text-brand shrink-0" />
                <span className="text-navy font-semibold text-sm">{b}</span>
              </div>
            ))}
          </div>
        )}

        {/* Items / prodotti */}
        {landing.items && landing.items.length > 0 && (
          <>
            <h2 className="mt-12 font-heading font-bold text-2xl text-navy">Modelli e offerte disponibili</h2>
            <div className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="landing-items">
              {landing.items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => { setForm((f) => ({ ...f, service: it.name })); document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid={`landing-item-${it.id}`}
                  className={`text-left bg-white rounded-2xl border p-5 soft-shadow transition-all hover:-translate-y-0.5 ${form.service === it.name ? "border-brand ring-2 ring-brand/30" : "border-line hover:border-brand"}`}
                >
                  {it.image_url && (
                    <div className="mb-4 h-40 rounded-xl bg-surface bg-contain bg-no-repeat bg-center" style={{ backgroundImage: `url(${it.image_url})` }} />
                  )}
                  <div className="font-heading font-bold text-navy">{it.name}</div>
                  {it.description && <div className="mt-1 text-sm text-navy-soft">{it.description}</div>}
                  <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                    {it.full_price && <span className="text-sm text-navy-soft line-through">{it.full_price}</span>}
                    {it.price && <span className="font-heading font-extrabold text-xl text-brand-dark">{it.price}</span>}
                    {it.full_price && it.price && <span className="text-[11px] font-bold uppercase tracking-wide bg-brand-light text-brand-darker px-2 py-0.5 rounded-full">con noi</span>}
                  </div>
                  {discountPct(it.full_price, it.price) !== null && (
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-brand text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid={`landing-item-saving-${it.id}`}>
                      Risparmi il {discountPct(it.full_price, it.price)}%
                    </div>
                  )}
                  {it.benefits && it.benefits.length > 0 && (
                    <ul className="mt-3 space-y-1.5">
                      {it.benefits.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-navy">
                          <CheckCircle2 className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Booking form */}
        <div id="prenota" className="mt-14 scroll-mt-24">
          {done ? (
            <div className="bg-brand-light/60 border border-brand/30 rounded-3xl p-10 text-center" data-testid="booking-success">
              <PartyPopper className="w-12 h-12 text-brand-dark mx-auto mb-4" />
              <h3 className="font-heading font-extrabold text-2xl text-navy">Prenotazione ricevuta!</h3>
              <p className="mt-2 text-navy-soft">Grazie {form.name.split(" ")[0]}, ti contatteremo il prima possibile al numero indicato.</p>
            </div>
          ) : (
            <div className="bg-white border border-line rounded-3xl soft-shadow-lg p-7 sm:p-10 max-w-2xl">
              <h3 className="font-heading font-extrabold text-2xl text-navy">Prenota la tua promozione</h3>
              <p className="mt-2 text-sm text-navy-soft">Lascia i tuoi dati: ti richiamiamo noi, senza impegno.</p>
              <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <label className="label-eyebrow text-navy-soft">Nome e cognome *</label>
                  <input value={form.name} onChange={set("name")} required data-testid="booking-name" className="w-full bg-surface border border-line focus:border-brand rounded-xl px-4 py-3 outline-none transition-colors text-navy" placeholder="Mario Rossi" />
                </div>
                <div className="sm:col-span-1">
                  <label className="label-eyebrow text-navy-soft">Telefono *</label>
                  <input value={form.phone} onChange={set("phone")} required type="tel" data-testid="booking-phone" className="w-full bg-surface border border-line focus:border-brand rounded-xl px-4 py-3 outline-none transition-colors text-navy" placeholder="333 1234567" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-eyebrow text-navy-soft">Email</label>
                  <input value={form.email} onChange={set("email")} type="email" data-testid="booking-email" className="w-full bg-surface border border-line focus:border-brand rounded-xl px-4 py-3 outline-none transition-colors text-navy" placeholder="latua@email.it" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-eyebrow text-navy-soft">Servizio d'interesse</label>
                  {services.length > 0 ? (
                    <select value={form.service} onChange={set("service")} data-testid="booking-service" className="w-full bg-surface border border-line focus:border-brand rounded-xl px-4 py-3 outline-none transition-colors text-navy">
                      <option value="">Seleziona…</option>
                      {services.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input value={form.service} onChange={set("service")} data-testid="booking-service" className="w-full bg-surface border border-line focus:border-brand rounded-xl px-4 py-3 outline-none transition-colors text-navy" placeholder="Es. Fibra, Mobile…" />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={submitting} data-testid="booking-submit" className="w-full bg-brand hover:bg-brand-dark text-white font-bold py-3.5 rounded-full transition-colors disabled:opacity-60">
                    {submitting ? "Invio…" : "Prenota ora"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
